// Timesheet period + hours rules. Pure functions over calendar dates — the API
// service enforces them; the web UI only mirrors them for UX.
//
// "Today" defaults to `businessDate()` throughout, and both week bounds are
// measured from it: the historical edit lock behind, the current week ahead. The
// bound ahead is per *week* — days later in the current week stay fillable, which
// is what lets TIME-14 open a fresh weekday at 8 h and a contractor submit on
// Friday for the week they are still in.

import { isWorkedType, type EditorStatus, type EntryType } from "./constants.js";
import {
  addDays,
  businessDate,
  fromYmd,
  isValidYmd,
  isWeekend,
  periodDays,
  periodHasWeekday,
  startOfWeek,
  utcDate,
  weekDays,
  ymd,
  type WeekSegment,
} from "./dates.js";
import { computeTotal, type EntryInput } from "./timesheet.js";

/**
 * The hours a single day may hold (TIME-6).
 *
 * Raised from 12 together with `MAX_HOURS_PER_WEEK`, which moved 60 → 80 in the
 * same change. The two are a pair and moving one alone is pointless: the lower
 * of them is always the real limit. Five 16-hour days is exactly 80, so the day
 * cap and the week cap meet instead of one shadowing the other.
 *
 * `NOTE_REQUIRED_ABOVE_HOURS` and `OVERTIME_CONFIRM_THRESHOLD` deliberately did
 * **not** move. Permitting a longer day is not the same as calling it ordinary:
 * anything past 8 h still has to say why, and a week past 40 h still has to be
 * confirmed. Those two are the record that a long week was intended.
 */
export const MAX_HOURS_PER_DAY = 16;
/**
 * The granularity of a day's hours (TIME-6). Whole hours.
 *
 * It was 0.5 until contractors asked for it not to be: half hours were a
 * precision nobody used and every timesheet had to step past.
 *
 * The **database is deliberately wider** than this. Its CHECK still reads
 * `[hours] * 2 = FLOOR([hours] * 2)`, because whole hours are a subset of half
 * hours — so no migration was needed, already-filed weeks holding 7.5 stay valid
 * and readable, and nothing rewrote a record that was correct when it was filed.
 * `api/test/constraints.test.ts` pins that gap on purpose. Editable weeks holding
 * a half hour are rounded when opened, which is the only place the old values
 * meet the new rule.
 */
export const HOURS_STEP = 1;
/** A week's hard ceiling. Paired with `MAX_HOURS_PER_DAY` — see the note there. */
export const MAX_HOURS_PER_WEEK = 80;
export const OVERTIME_CONFIRM_THRESHOLD = 40;

/**
 * The contractor's per-day note. Short by design — it explains one day ("client
 * go-live, stayed late"), so a sentence is the whole job. Deliberately *not* the
 * same number as an admin's return-for-changes note below: that one has to carry
 * an explanation of what to fix, and 100 characters cannot.
 */
export const MAX_NOTE_LENGTH = 100;
/** The admin's review note, on returning a week for changes. */
export const MAX_COMMENT_LENGTH = 500;

/**
 * Worked hours in a day past which the entry has to say why.
 *
 * Not `HOURS_PER_DAY` below, which is the day-rate → hourly billing divisor and
 * equals 8 by coincidence rather than by agreement. Repricing a contract must
 * not silently move the threshold at which a contractor is asked to explain a
 * long day.
 */
export const NOTE_REQUIRED_ABOVE_HOURS = 8;

/**
 * The shape an hours box may hold on its way to a legal value.
 *
 * Two digits *is* `MAX_HOURS_PER_DAY`, and the absence of any decimal part *is*
 * `HOURS_STEP` — the same two rules `validateWeekEntries` applies below, written
 * in the one form no regex can derive from a constant. `rules.test.ts` walks
 * every valid step to keep the two honest if either number ever moves.
 *
 * The decimal point is refused outright rather than admitted and then rejected:
 * with whole hours there is no legal value a "." is on the way to, so accepting
 * the keystroke would only ever be a detour to an error.
 */
const HOURS_TEXT = /^\d{0,2}$/;

/**
 * What an hours box is allowed to contain, as text — or `null` for a keystroke
 * that must be refused.
 *
 * `min`/`max`/`step` on `<input type="number">` gate the spinner arrows and
 * nothing else: outside a `<form>` there is no constraint validation, so typing
 * was unbounded and a contractor filed 34567654345 hours. This is the arrows'
 * own rule expressed over the text, so the box can only ever hold a value the
 * arrows could have produced — which puts the API's HOURS_RANGE/HOURS_STEP 400
 * out of the keyboard's reach rather than merely leaving it survivable.
 *
 * `""` is an empty box, which is 0 hours. A number input also reports empty for
 * text it cannot parse — "6." on the way to "6.5" — but that is the caller's to
 * tell apart (`validity.badInput`), because only the caller can see the text.
 */
export function acceptHoursText(raw: string): number | null {
  if (raw === "") return 0;
  if (!HOURS_TEXT.test(raw)) return null;
  const hours = Number(raw);
  // `Number.isFinite` catches a lone ".", which the pattern admits and a number
  // input can never produce.
  return Number.isFinite(hours) && hours <= MAX_HOURS_PER_DAY ? hours : null;
}

/** Assignment daily rate: never negative, and must fit `Decimal(10,2)`. */
export const MAX_DAILY_RATE = 99_999.99;
export const RATE_STEP = 0.01;
/**
 * A billable day. The divisor that turns an agreed day rate into the hourly rate
 * we actually store (RATE-3), and the multiplier that renders it back.
 *
 * It was previously a bare `/ 8` sitting in the middle of the reports query.
 */
export const HOURS_PER_DAY = 8;
/**
 * The first month whose timesheets were kept in the portal. An invoice for an
 * earlier month has nothing to be compared with, so its timesheet checks are
 * skipped (CINV-26) rather than reading as a mismatch for ever. The API hands
 * it to the engine as an input, so moving it re-checks what it touches.
 */
export const TIMESHEETS_KEPT_FROM = "2026-08";
/** The stored (hourly) bound, so a day rate at its own maximum still fits. */
export const MAX_HOURLY_RATE = MAX_DAILY_RATE / HOURS_PER_DAY;
/** Weeks older than this (before the current week) are read-only for contractors. */
export const LOCK_AFTER_WEEKS = 8;
/** Days after the week's Monday when an unsubmitted week becomes overdue — the Wednesday after, business-local (TIME-10). */
const OVERDUE_AFTER_DAYS = 9;

/**
 * Ceilings on the bulk/range operations. Each exists because the request has to
 * finish inside Azure Static Web Apps' ~45s gateway limit — past it the SPA gets
 * a non-JSON 502 instead of our error envelope, so a "too big" request must be
 * refused in our own words rather than discovered as a timeout.
 */
export const MAX_BULK_APPROVE_WEEKS = 25;
export const MAX_EXPORT_ROWS = 5_000;
export const MAX_CHASE_WEEKS = 12;

/**
 * How long an approval can be undone.
 *
 * Undo is a *compensating* action, not a deferred send: an admin who approves
 * and closes the tab must get an approval. So the window is short and exists
 * only to catch the misclick.
 */
export const UNDO_WINDOW_MINUTES = 10;

/** Whether an approval is still inside the undo window. */
export function withinUndoWindow(reviewedAt: Date, now: Date = new Date()): boolean {
  const elapsed = now.getTime() - reviewedAt.getTime();
  return elapsed >= 0 && elapsed <= UNDO_WINDOW_MINUTES * 60 * 1000;
}

/**
 * The window a contractor may file a week against: the **engagement**, i.e. the
 * assignment's own dates tightened by the project's (assignment ∩ project). A
 * contractor assigned "from today" on a project that started months ago is only
 * engaged from today, not from the project start — so both bounds narrow the
 * fillable range. Named `ProjectBounds` for history; it is the engagement window.
 */
export type ProjectBounds = {
  /** Engagement start date (UTC midnight) or null when open-ended. */
  startDate: Date | null;
  /** Engagement end date (UTC midnight, inclusive) or null when open-ended. */
  endDate: Date | null;
  /** Whether the contractor's assignment to the project is active. */
  assignmentActive: boolean;
};

/** The later of two open-ended (nullable) dates — null means "unbounded". */
function laterOf(a: Date | null, b: Date | null): Date | null {
  if (!a) return b;
  if (!b) return a;
  return a.getTime() >= b.getTime() ? a : b;
}

/** The earlier of two open-ended (nullable) dates — null means "unbounded". */
function earlierOf(a: Date | null, b: Date | null): Date | null {
  if (!a) return b;
  if (!b) return a;
  return a.getTime() <= b.getTime() ? a : b;
}

/**
 * The engagement window a contractor may actually file against: the assignment's
 * own dates, tightened by the project's — `max(starts) … min(ends)`, with null on
 * either side meaning "open-ended". This is the single source of truth for the
 * bound; the API feeds it to `weekEditability`/`validateWeekEntries`, and the
 * contractor calendar uses it to know which weeks are inside the engagement.
 */
export function engagementBounds(a: {
  active: boolean;
  assignmentStart: Date | null;
  assignmentEnd: Date | null;
  projectStart: Date | null;
  projectEnd: Date | null;
}): ProjectBounds {
  return {
    assignmentActive: a.active,
    startDate: laterOf(a.assignmentStart, a.projectStart),
    endDate: earlierOf(a.assignmentEnd, a.projectEnd),
  };
}

export type EditBlockReason =
  | "SUBMITTED" // pending review — locked
  | "APPROVED" // finalised — locked
  | "FUTURE" // the week hasn't started yet (TIME-4)
  | "LOCKED" // older than LOCK_AFTER_WEEKS (unless changes were requested)
  | "OUT_OF_ENGAGEMENT" // week entirely outside the project's date range
  | "INACTIVE_ASSIGNMENT"; // assignment deactivated

export type WeekEditability =
  { editable: true } | { editable: false; reason: EditBlockReason };

/** The days one timesheet row covers: a whole week, or one month's part of it (TIME-1). */
export type TimesheetPeriod = Pick<WeekSegment, "periodStart" | "periodEnd">;

/** Whether the given week overlaps the project date range at all. */
export function weekOverlapsProject(monday: Date, bounds: ProjectBounds): boolean {
  return periodOverlapsProject({ periodStart: monday, periodEnd: addDays(monday, 6) }, bounds);
}

/**
 * Whether a period overlaps the project date range at all.
 *
 * The half of a split week matters here where it matters nowhere else in
 * editability: an engagement ending on 30 September leaves the October half of
 * that week outside it, though the week as a whole overlaps.
 */
export function periodOverlapsProject(period: TimesheetPeriod, bounds: ProjectBounds): boolean {
  if (bounds.startDate && period.periodEnd.getTime() < utcDate(bounds.startDate).getTime())
    return false;
  if (bounds.endDate && period.periodStart.getTime() > utcDate(bounds.endDate).getTime())
    return false;
  return true;
}

/**
 * Can a contractor edit this week's sheet right now?
 * Order matters: status locks trump period locks so the UI shows the most
 * useful reason (an APPROVED old week says "approved", not "locked").
 */
export function weekEditability(
  monday: Date,
  status: EditorStatus,
  bounds: ProjectBounds,
  today: Date = businessDate(),
  /**
   * The half of a split week being asked about (TIME-1); the whole week when
   * absent. Only the engagement test reads it — every other lock keys off the
   * Monday, which both halves share, so they lock and unlock together.
   */
  period?: TimesheetPeriod
): WeekEditability {
  if (status === "SUBMITTED") return { editable: false, reason: "SUBMITTED" };
  if (status === "APPROVED") return { editable: false, reason: "APPROVED" };

  if (!bounds.assignmentActive) return { editable: false, reason: "INACTIVE_ASSIGNMENT" };
  if (!(period ? periodOverlapsProject(period, bounds) : weekOverlapsProject(monday, bounds)))
    return { editable: false, reason: "OUT_OF_ENGAGEMENT" };

  const currentMonday = startOfWeek(today);

  /**
   * A week that hasn't started is not the contractor's to fill yet (TIME-4).
   *
   * Deliberately no `CHANGES_REQUESTED` exemption, unlike the age lock below. That
   * escape hatch exists because an old week can only be re-opened by an admin, so
   * without it a contractor would be stuck; this lock lifts on its own the Monday
   * the week arrives, and there is nothing for an admin to usefully return on a
   * week nobody could have worked.
   *
   * `today` must stay business-local: at 9am Monday in Sydney it is still Sunday
   * in UTC, so a UTC-derived today makes the *real* current week look like next
   * week and rejects it. That was a live bug — see test/business-time.test.ts.
   */
  if (monday.getTime() > currentMonday.getTime())
    return { editable: false, reason: "FUTURE" };

  // An admin change request re-opens the sheet regardless of age.
  const lockBefore = addDays(currentMonday, -7 * LOCK_AFTER_WEEKS);
  if (monday.getTime() < lockBefore.getTime() && status !== "CHANGES_REQUESTED")
    return { editable: false, reason: "LOCKED" };

  return { editable: true };
}

/** Due/overdue state for badges. Only unsubmitted sheets can be due/overdue. */
export type SubmissionState = "none" | "due" | "overdue";

export function submissionState(
  monday: Date,
  status: EditorStatus,
  now: Date = businessDate()
): SubmissionState {
  if (status === "SUBMITTED" || status === "APPROVED") return "none";
  const weekEnded = now.getTime() >= addDays(monday, 7).getTime();
  if (!weekEnded) return "none";
  const overdueAt = addDays(monday, OVERDUE_AFTER_DAYS);
  return now.getTime() >= overdueAt.getTime() ? "overdue" : "due";
}

/**
 * Whether a week is one an admin should chase, and how loudly.
 *
 * Deliberately adds no date math of its own — it is `submissionState` (TIME-10)
 * gated by the same engagement checks `weekEditability` already applies. A week
 * nobody could have filed is not a week anybody is late on: an inactive
 * assignment, a week outside the engagement, and a week that hasn't ended all
 * return "none".
 *
 * Kept beside those rules rather than in the chase service so the overdue
 * threshold is never re-derived — in SQL least of all.
 */
export function chaseableWeek(
  monday: Date,
  bounds: ProjectBounds,
  status: EditorStatus,
  today: Date = businessDate(),
  /**
   * The half of a split week (TIME-1); the whole week when absent. A half the
   * engagement does not reach is nobody's to file, and neither is a half with
   * no weekday in it — Sunday 1 November is a weekend, not a missing timesheet.
   */
  period?: TimesheetPeriod
): SubmissionState {
  if (!bounds.assignmentActive) return "none";
  if (period) {
    if (!periodOverlapsProject(period, bounds)) return "none";
    if (!periodHasWeekday(period)) return "none";
  } else if (!weekOverlapsProject(monday, bounds)) return "none";
  if (monday.getTime() > startOfWeek(today).getTime()) return "none";
  return submissionState(monday, status, today);
}

export type EntryError = { index: number; code: string; message: string };

export type WeekValidation =
  | { ok: true; total: number }
  | { ok: false; code: string; message: string; entryErrors?: EntryError[] };

/**
 * Validate one timesheet row's entries against the period + hour rules: one
 * entry per day of its period, in order (TIME-3) — the whole week, or one
 * month's part of it (TIME-1).
 * Assumes shape/type validation (zod) already passed; this checks the
 * cross-field business rules.
 */
export function validateWeekEntries(
  monday: Date,
  entries: EntryInput[],
  opts: {
    submit: boolean;
    confirmOvertime: boolean;
    bounds: ProjectBounds;
    today?: Date;
    /**
     * What the contractor's *other* projects already hold for the same week —
     * hours per date, and the dates one of them has already explained.
     *
     * The note rule (TIME-12) is about the **day**, not about the sheet, and a
     * day can be split across projects: 5 h + 5 h is a long day with no single
     * entry that looks long, and a note on either row answers for both. Neither
     * fact is visible from one sheet, so the caller who *can* see them hands them
     * in — exactly as it already hands in `bounds`.
     *
     * Absent means "this is the only sheet", which is the truth for a contractor
     * on one engagement and a safe default for anyone who cannot look it up.
     */
    otherHoursByDate?: ReadonlyMap<string, number>;
    notedDates?: ReadonlySet<string>;
    /**
     * The days this row covers (TIME-1). Absent is the whole week. Zod cannot
     * check the count — it does not know where the month falls — so this is
     * where "one entry per day of the period" is enforced.
     */
    period?: TimesheetPeriod;
    /**
     * Why a sheet being submitted holds no worked hours (TIME-15). Without it an
     * empty submit is refused; with it, the sheet is filed as a nil timesheet —
     * the contractor's answer for an engagement they did no work on that week.
     */
    nilNote?: string | null;
  }
): WeekValidation {
  const expected = (opts.period ? periodDays(opts.period) : weekDays(monday)).map(ymd);

  if (entries.length !== expected.length) {
    return {
      ok: false,
      code: "BAD_WEEK",
      message:
        expected.length === 7
          ? "Timesheet must cover the full week."
          : `Timesheet must cover ${expected[0]} to ${expected[expected.length - 1]}, one entry per day.`,
    };
  }

  const entryErrors: EntryError[] = [];
  entries.forEach((e, i) => {
    if (!isValidYmd(e.date) || e.date !== expected[i]) {
      entryErrors.push({
        index: i,
        code: "BAD_DATE",
        message: `Row ${i + 1} must be ${expected[i]}.`,
      });
      return;
    }
    const day = fromYmd(e.date);
    const hours = e.hours;

    if (!Number.isFinite(hours) || hours < 0 || hours > MAX_HOURS_PER_DAY) {
      entryErrors.push({
        index: i,
        code: "HOURS_RANGE",
        message: `Hours must be between 0 and ${MAX_HOURS_PER_DAY}.`,
      });
    } else if (!Number.isInteger(hours / HOURS_STEP)) {
      entryErrors.push({
        index: i,
        code: "HOURS_STEP",
        // Spelled out rather than interpolated from `HOURS_STEP`: it is 1, and
        // "Hours must be in 1 increments" is not a sentence. If the step ever
        // moves off a whole number this message has to be rewritten by hand —
        // `rules.test.ts` walks every accepted step, so that will not go unnoticed.
        message: "Hours must be whole numbers.",
      });
    }

    if (!isWorkedType(e.type) && hours !== 0) {
      entryErrors.push({
        index: i,
        code: "NON_WORKED_HOURS",
        message: "Only worked days can have hours.",
      });
    }

    if (hours > 0) {
      const { startDate, endDate } = opts.bounds;
      const outside =
        (startDate && day.getTime() < utcDate(startDate).getTime()) ||
        (endDate && day.getTime() > utcDate(endDate).getTime());
      if (outside) {
        entryErrors.push({
          index: i,
          code: "OUT_OF_PROJECT",
          message: "This date is outside the project engagement.",
        });
      }
    }

    if (e.comment.length > MAX_NOTE_LENGTH) {
      entryErrors.push({
        index: i,
        code: "COMMENT_LENGTH",
        message: `Notes are limited to ${MAX_NOTE_LENGTH} characters.`,
      });
    }

    // An unusual day has to say why — but only when it is being filed. A draft is
    // someone still typing, and refusing to hold their work until they have
    // justified it is how a note becomes something to type past rather than mean.
    //
    // Measured on the day's contractor-wide hours and answered by a note on any
    // of its rows, so the rule reads the same whether one project or three are
    // carrying the day. `otherHoursByDate`/`notedDates` are what make that
    // possible from inside a single sheet.
    if (
      opts.submit &&
      isWorkedType(e.type) &&
      hours > 0 &&
      e.comment.trim() === "" &&
      !opts.notedDates?.has(e.date)
    ) {
      const combined = hours + (opts.otherHoursByDate?.get(e.date) ?? 0);
      if (combined > NOTE_REQUIRED_ABOVE_HOURS) {
        entryErrors.push({
          index: i,
          code: "NOTE_REQUIRED",
          message: `Add a note explaining more than ${NOTE_REQUIRED_ABOVE_HOURS} hours on this day.`,
        });
      } else if (isWeekend(day)) {
        entryErrors.push({
          index: i,
          code: "NOTE_REQUIRED",
          message: "Add a note explaining this weekend work.",
        });
      }
    }
  });

  if (entryErrors.length > 0) {
    return {
      ok: false,
      code: "INVALID_ENTRIES",
      message: "Some entries need fixing before saving.",
      entryErrors,
    };
  }

  const total = computeTotal(entries);
  if (total > MAX_HOURS_PER_WEEK) {
    return {
      ok: false,
      code: "WEEK_CAP",
      message: `A week can't exceed ${MAX_HOURS_PER_WEEK} worked hours.`,
    };
  }

  if (opts.submit) {
    if (total <= 0 && !opts.nilNote?.trim()) {
      return {
        ok: false,
        code: "EMPTY_SUBMIT",
        message: "Add some hours, or say why there are none, before submitting.",
      };
    }
    if (total > OVERTIME_CONFIRM_THRESHOLD && !opts.confirmOvertime) {
      return {
        ok: false,
        code: "CONFIRM_OVERTIME",
        message: `Over ${OVERTIME_CONFIRM_THRESHOLD} hours — please confirm the overtime is correct.`,
      };
    }
  }

  return { ok: true, total };
}

/** Sum of hours typed for entry types (used by UI totals). */
export type { EntryType };

// --- Contractor invoices (CINV) ----------------------------------------------

/**
 * The largest invoice PDF the portal accepts (CINV-13). A monthly invoice is one
 * to three pages; ten mebibytes is generous for a scan and small enough that the
 * request stays comfortably inside the gateway window (SEC-6).
 */
export const MAX_INVOICE_PDF_BYTES = 10 * 1024 * 1024;
/** The bank/remittance reference an admin records when marking an invoice paid. */
export const MAX_PAYMENT_REFERENCE_LENGTH = 100;
/**
 * How close two hour figures must be to count as equal (CINV-12). Timesheets
 * hold whole hours and invoices are read to two decimals, so anything past a
 * hundredth is rounding, not a claim.
 */
/**
 * How long a read may sit PENDING before it is treated as lost. The worker marks
 * a read FAILED on its last queue attempt (5 × a 90 s call plus the queue's
 * back-off settles well inside 15 minutes), so PENDING past this means the
 * message never ran — and only then may an admin re-run it without paying for a
 * read that is still in flight (CINV-5).
 */
export const INVOICE_EXTRACTION_STUCK_AFTER_MS = 30 * 60_000;

export const INVOICE_HOURS_TOLERANCE = 0.01;
/** Money compares to the cent. */
export const INVOICE_MONEY_TOLERANCE = 0.01;
/**
 * Below this model confidence the verdict is `NEEDS_REVIEW` even when every
 * number matches (CINV-12): matching figures read off a PDF the model was unsure
 * of is a coincidence, not a reconciliation.
 */
export const INVOICE_CONFIDENCE_REVIEW_BELOW = 0.8;
/** GST in Australia (TAX-AU-1). */
export const AU_GST_RATE_PCT = 10;

/**
 * The most line items one invoice keeps. The model's array is unbounded, and
 * every line becomes stored JSON, a tag an admin can set and a term in the
 * project rollup; past this an invoice is a statement, not something to review
 * line by line. Lines beyond it are dropped with a note, never silently.
 */
export const MAX_INVOICE_LINES = 200;
